import { http, toQuery } from '@/lib/apiClient';
import type { LifecycleTimelineStage } from '@/api/lifecycleTimeline';
import type { WorkflowInstance, WorkflowTask } from '@/api/workflows.api';

export interface PagedFinanceResult<T> { items: T[]; page: number; pageSize: number; totalCount: number }

export interface RetentionRecord {
  id: string; vendorId: string; vendorName?: string | null; invoiceId: string; invoiceNumber?: string | null;
  amount: number; percentage?: number | null; currencyCode?: string | null; status: string;
  releasedAt?: string | null; createdAt?: string | null;
}

export interface AdvanceRecord {
  id: string; vendorId: string; vendorName?: string | null; poId?: string | null; poNumber?: string | null;
  amount: number; availableAmount?: number | null; adjustedAmount?: number | null; currencyCode?: string | null;
  status: string; createdAt?: string | null;
}

export interface PaymentAllocation {
  id?: string; invoiceId: string; invoiceNumber?: string | null; poNumber?: string | null; amount: number;
  invoicePayable?: number | null; payableAmount?: number | null; outstandingAfterPayment?: number | null; outstandingAmount?: number | null; createdAt?: string | null;
}

export interface Payment {
  id: string; paymentNumber?: string | null; invoiceId: string; invoiceNumber?: string | null; poId?: string | null;
  poNumber?: string | null; vendorId: string; vendorName?: string | null; amount: number; currencyCode?: string | null;
  paymentDate: string; utrNumber?: string | null; bankReference?: string | null; status: string;
  createdAt?: string | null; submittedAt?: string | null; approvedAt?: string | null; paidAt?: string | null;
  createdBy?: string | null; createdByName?: string | null; approvedByName?: string | null; remarks?: string | null;
  receiptUrl?: string | null; proofUrl?: string | null; allocations?: PaymentAllocation[] | null; workflowInstance?: WorkflowInstance | null;
  lifecycleTimeline?: LifecycleTimelineStage[] | null;
}

export interface PaymentApprovalDetail {
  payment: Payment; workflowInstance?: WorkflowInstance | null; currentTask?: WorkflowTask | null;
  actions?: Record<string, boolean>;
}

export const financeApi = {
  retentions: (query: { page?: number; pageSize?: number; vendorId?: string; invoiceId?: string; status?: string } = {}) =>
    http.get<PagedFinanceResult<RetentionRecord>>(`/api/v1/finance/retentions${toQuery({ page: query.page ?? 1, pageSize: query.pageSize ?? 50, vendorId: query.vendorId, invoiceId: query.invoiceId, status: query.status })}`),
  advances: (query: { page?: number; pageSize?: number; vendorId?: string; poId?: string; status?: string } = {}) =>
    http.get<PagedFinanceResult<AdvanceRecord>>(`/api/v1/finance/advances${toQuery({ page: query.page ?? 1, pageSize: query.pageSize ?? 50, vendorId: query.vendorId, poId: query.poId, status: query.status })}`),
  createAdvance: (body: { vendorId: string; poId?: string | null; amount: number; status: string }) =>
    http.post<AdvanceRecord>('/api/v1/finance/advances', body),
  payments: (query: { page?: number; pageSize?: number; vendorId?: string; invoiceId?: string; status?: string } = {}) =>
    http.get<PagedFinanceResult<Payment>>(`/api/v1/payments${toQuery({ page: query.page ?? 1, pageSize: query.pageSize ?? 50, vendorId: query.vendorId, invoiceId: query.invoiceId, status: query.status })}`),
  payment: (id: string) => http.get<Payment>(`/api/v1/payments/${id}`),
  createPayment: (body: { invoiceId: string; amount: number; paymentDate: string; utrNumber?: string; bankReference?: string; remarks?: string }) =>
    http.post<Payment>('/api/v1/payments', body),
  submitPayment: (id: string) => http.post<Payment>(`/api/v1/payments/${id}/submit`),
  paymentApprovalTask: (taskId: string) => http.get<PaymentApprovalDetail>(`/api/v1/payments/approval-tasks/${taskId}`),
};
